import {
  Box,
  capitalize,
  Grid2,
  IconButton,
  Typography,
  useTheme
} from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import {
  AddMembersHeader,
  CaretDownIcon,
  DatePickerSmall,
  GoTodayIcon,
  GroupChannelIcon,
  viewModesIcon
} from 'assets/icons'
import ContainerWithBottomBadgeIcon from 'components/containers/ContainerWithBottomBadgeIcon'
import GroupPopup, { GroupTab } from 'components/popups/groupInfo/GroupPopup'
import GroupTitle from 'components/popups/groupInfo/GroupTitle'
import { useCalendar } from 'components/providers/CalendarProvider'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import ObserveService from 'services/db/Observe'
import { isUserLeft } from 'services/db/utils/GroupUtils'
import LocalStorage from 'services/LocalStorage'
import { AccountDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { Option } from 'types/components/form'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier } from 'utils/api/accounts'
import {
  defaultGroupImage,
  getActiveParticipants,
  groupsToImages,
  isAddingMemberAllowedForGroup,
  isGroupChannel,
  isGroupOutOfTier,
  isUserActiveInGroup
} from 'utils/api/groups'
import NamesStrip from './NamesStrip'
import * as Styled from './styled'
import { t } from 'i18next'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import i18n from 'i18n'
import { SEARCH_MODE } from 'components/popups/searchItems/SearchPopup'
import SearchPopupButton from 'components/SearchPopupButton'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { isGuest } from 'utils/user'
import { isMobileDevice } from 'utils/constants'
import Avatar from 'components/badges/Avatar'
import ViewDatesPicker from './ViewDatesPicker'
import { DateRange } from 'react-day-picker'
import { isTrulyEmpty } from 'utils/object'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useTour } from '@reactour/tour'
import { useAppMode } from 'components/providers/AppModeProvider'
import { isConvertedGroup } from 'utils/api/group_state'
import Logger from 'services/Logger'
import usePersistState, { useLocalStorageState } from 'hooks/usePersistState'
import { hide } from '@floating-ui/react'

export const RANGE: Option[] = [
  { label: capitalize(t('day_one')), value: CalendarRange.DAY },
  { label: capitalize(t('week_one')), value: CalendarRange.WEEK },
  { label: capitalize(t('month_one')), value: CalendarRange.MONTH },
  { label: capitalize(t('list')), value: CalendarRange.LISTWEEK },
  { label: capitalize(t('dayList')), value: CalendarRange.LISTDAY },
  {
    label: capitalize(t('cards', { defaultValue: 'Cards' })),
    value: CalendarRange.CARDS
  }
]

export const dateFormat = 'YYYY-MM-DD'

interface CalendarHeaderProps {
  account?: Model<AccountDoc>
  groups?: Model<GroupDoc>[]
  userSettings?: Model<UserSettingsDoc>
  currentGroup?: Model<GroupDoc>
  groupID: string
  currentView?: CalendarRange
  title?: string
  embedMode?: boolean
  dateRangeSelected?: (selected: Date | DateRange | undefined) => void
  canUseCardsView?: boolean
  groupIds?: string[]
}

function CalendarHeader(props: CalendarHeaderProps) {
  const calendar = useCalendar()

  const appMode = useAppMode()

  const tour = useTour()
  const tfmTour = useTfmTourProvider()

  return (
    <Styled.CalendarHeader
      isMobile={isMobileDevice()}
      isCondensedHeight={appMode.isCondensedHeight}
      className="calendar-header"
    >
      {props.currentGroup?._id ? (
        <Styled.CalendarHeaderInfo
          id="calendar-info-calendar"
          onClick={() => {
            calendar.onGroupInfo(props.currentGroup?._id)
            setTimeout(() => {
              if (
                tour.setSteps &&
                tfmTour.getStepsByMode &&
                !tfmTour.isPlaying
              ) {
                const steps = tfmTour.getStepsByMode(
                  ONBOARDING_MODE.FIRST_LOGIN,
                  () => {
                    const clearSteps = tfmTour.getStepsByMode!(
                      ONBOARDING_MODE.FIRST_LOGIN
                    )

                    return (
                      tfmTour.isStepCompleted?.('#group-details-popup') ===
                        false &&
                      clearSteps.length > 0 &&
                      clearSteps[0].selector === '#group-details-popup'
                    )
                  }
                )

                if (steps.length === 0) return

                LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
                tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)
                tour.setSteps(steps)
                tour.setCurrentStep(0)
                tour.setIsOpen(steps.length > 0)
              }
            }, 250)
          }}
        >
          <GroupPhotoElement {...props} />

          <GroupTitleElement {...props} />
        </Styled.CalendarHeaderInfo>
      ) : (
        <Styled.CalendarHeaderInfo>
          <Styled.AllAccountBadge
            groups={props.groups}
            userSettings={props.userSettings}
            images={groupsToImages(props.groups)}
            groupIds={props.groupIds}
          />
          <Styled.CalendarHeaderTitleContainer>
            <Styled.CalendarHeaderTitle
              sx={{
                fontSize: appMode.isCondensedHeight ? '16px' : '1.5rem',
                marginRight: i18n.dir() === 'ltr' ? 0 : 8,
                cursor: 'default'
              }}
            >
              {props.title ?? t('allCalendarsButton')}
            </Styled.CalendarHeaderTitle>
          </Styled.CalendarHeaderTitleContainer>
        </Styled.CalendarHeaderInfo>
      )}

      <Styled.CalendarHeaderActions isMobile={isMobileDevice()}>
        <NextPrevButtons dateRangeSelected={props.dateRangeSelected} />
        <NavButtons {...props} />
        <Box
          display={'flex'}
          flexDirection={'row'}
          height={'100%'}
          alignContent={'center'}
          justifyContent={'center'}
          alignItems={'center'}
          justifyItems={'center'}
          mt={appMode.isCondensedHeight ? '-2px' : 0}
        >
          <GroupcalTooltip title={t('backToToday')}>
            <Styled.TodayButton
              id="go-today-button"
              isCondensedWidth={appMode.isCondensedWidth}
              isIconMode={appMode.isCondensedWidth || appMode.isCondensedHeight}
              onClick={calendar.onGoToToday}
            >
              <span className="today-text">{t('today')}</span>
              <GoTodayIcon
                width={20}
                height={20}
                className="today-image"
                style={{
                  display:
                    appMode.isCondensedWidth || appMode.isCondensedHeight
                      ? 'block'
                      : 'none'
                }}
              />
            </Styled.TodayButton>
          </GroupcalTooltip>
          {isGuest() || props.embedMode ? (
            <></>
          ) : (
            <Box
              ml={
                !appMode.isCondensedWidth && i18n.dir() === 'ltr' ? '1rem' : 0
              }
              mr={
                !appMode.isCondensedWidth && i18n.dir() === 'rtl' ? '1rem' : 0
              }
              mt={appMode.isCondensedHeight ? '0.2rem' : 0}
            >
              <SearchPopupButton
                spanId="events-search-button"
                groupId={props.currentGroup?._id}
                mode={SEARCH_MODE.OnlyEvents}
                textfieldPlaceholder={
                  (calendar.groupServerID.length > 0
                    ? t('searchItemsIn', {
                        what: t('events').toLowerCase(),
                        where: props.currentGroup?.Name ?? ''
                      })
                    : is24meApp()
                      ? t('searchItems', {
                          what: t('eventsTasksNotes').toLowerCase()
                        })
                      : t('searchItems', {
                          what: t('events').toLowerCase()
                        })) ?? ''
                }
              />
            </Box>
          )}
        </Box>
      </Styled.CalendarHeaderActions>
    </Styled.CalendarHeader>
  )
}

function NavButtons(props: CalendarHeaderProps) {
  const calendar = useCalendar()
  const appMode = useAppMode()

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
      <Styled.Separator isMobile={isMobileDevice()} />
      <Styled.CalendarHeaderActionsCell>
        <Styled.RangeSelect
          sx={{ direction: 'ltr' }}
          variant="no-border"
          id="calendar-view-select"
          placeholder="Range"
          className={`select-view ${calendar.view}`}
          size="lg"
          showIcon
          customIcon={viewModesIcon}
          options={RANGE.filter((option) =>
            props.canUseCardsView ? true : option.value !== CalendarRange.CARDS
          )}
          value={calendar.view}
          onChange={(e) => {
            console.log(calendar.ref)
            calendar.onView(e?.value as CalendarRange)
          }}
        />
        <Styled.Separator isMobile={isMobileDevice()} />
      </Styled.CalendarHeaderActionsCell>
    </Box>
  )
}

function GroupTitleElement(props: CalendarHeaderProps) {
  const calendar = useCalendar()
  const theme = useTheme()

  const [groupPoupOpen, setGroupPopupOpen] = useState(false)
  const appMode = useAppMode()

  function toggleGroupPopupState(b: boolean) {
    setGroupPopupOpen(b)
  }

  useEffect(() => {
    console.log('CalendarInfo', calendar.groupServerID)
    if (isTrulyEmpty(calendar.groupServerID)) hideGroup()
  }, [calendar.groupServerID])

  const isUserNotActive =
    props.currentGroup &&
    !isUserActiveInGroup(
      props.currentGroup,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )

  const showGroup = () => {
    if (isGuest()) return
    toggleGroupPopupState(true)
    setOpenedTab(undefined)
  }

  const hideGroup = () => {
    toggleGroupPopupState(false)
  }

  const readOnly = props.embedMode || isGuest()

  const [openedTab, setOpenedTab] = useState<GroupTab | undefined>(
    GroupTab.PUBLIC_INFO
  )

  Logger.blue('CalendarHeader:', props, calendar, groupPoupOpen)

  return (
    <Styled.CalendarHeaderTitleContainer className="calendar-info-wrapper">
      <Styled.CalendarHeaderTitle
        sx={{
          '&:hover': {
            textDecoration: 'underline',
            textDecorationThickness: '0.5px',
            textDecorationColor: 'var(--color-border-default)',
            textUnderlineOffset: '2px'
          }
        }}
      >
        <Box display={'flex'} alignItems={'center'} gap={'0.25rem'}>
          <Box minWidth={0} overflow={'hidden'}>
            {props.title ? (
              <Typography fontSize={'1.5rem'} sx={{ cursor: 'pointer' }} noWrap>
                {props.title}
              </Typography>
            ) : (
              <GroupPopup
                openedTab={
                  isTrulyEmpty(LocalStorage.get(LocalStorageKey.ADDING_GROUP))
                    ? openedTab
                    : GroupTab.SHARING_MEMBERS
                }
                setOpen={toggleGroupPopupState}
                isOpen={
                  (groupPoupOpen || !isTrulyEmpty(calendar.groupInfo)) &&
                  calendar.groupServerID === props.currentGroup?._id
                }
                groupID={props.currentGroup?._id}
                group={props.currentGroup}
                userSettings={props.userSettings}
                account={props.account}
              >
                <GroupcalTooltip
                  hidden={isGuest() || groupPoupOpen}
                  title={t('clickToSeeCalendarInfo')}
                >
                  <Box onClick={showGroup} sx={{ cursor: 'pointer' }}>
                    <GroupTitle
                      group={props.currentGroup}
                      iconHeight={'20px'}
                    />
                  </Box>
                </GroupcalTooltip>
              </GroupPopup>
            )}
          </Box>
          {!isGuest() &&
            props.currentGroup &&
            isAddingMemberAllowedForGroup(props.currentGroup) &&
            isGroupcalApp() && (
              <GroupcalTooltip title={t('addMembersToThisCalendar')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setOpenedTab(GroupTab.SHARING_MEMBERS)
                    toggleGroupPopupState(true)
                    calendar.onGroupInfo(props.currentGroup?._id)
                  }}
                  aria-label={t('clickToSeeCalendarInfo')}
                  sx={{
                    padding: 0.25,
                    '& svg': {
                      transition: 'all 0.2s ease'
                    },
                    '& svg rect': {
                      fill: theme.palette.surface.active as string
                    },
                    '& svg path': {
                      stroke: theme.palette.getContrastText(
                        theme.palette.surface.active as string
                      )
                    },
                    '&:hover svg rect': {
                      fill: theme.palette.surface.hover as string
                    },
                    '&:hover svg path': {
                      stroke: theme.palette.getContrastText(
                        theme.palette.surface.hover as string
                      )
                    }
                  }}
                >
                  <AddMembersHeader width={24} height={24} />
                </IconButton>
              </GroupcalTooltip>
            )}
        </Box>
      </Styled.CalendarHeaderTitle>

      {readOnly || isGuest() || appMode.isCondensedHeight ? (
        <></>
      ) : (
        <GroupcalTooltip
          hidden={isGuest() || groupPoupOpen}
          title={t('addMembersToThisCalendar')}
        >
          <Styled.CalendarHeaderSubtitle
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setOpenedTab(
                isGroupcalApp()
                  ? GroupTab.SHARING_MEMBERS
                  : GroupTab.PUBLIC_INFO
              )
              toggleGroupPopupState(true)
              calendar.onGroupInfo(props.currentGroup?._id)
            }}
            color={isUserNotActive ? 'var(--color-negative)' : undefined}
          >
            {isUserNotActive && !isGuest() ? (
              isUserLeft(
                props.currentGroup,
                LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
              ) ? (
                'You left the shared calendar'
              ) : (
                'You have been removed from the shared calendar'
              )
            ) : readOnly ? (
              <></>
            ) : (
              props.currentGroup && (
                <NamesStrip
                  group={props.currentGroup}
                  participantIds={Object.keys(
                    getActiveParticipants(
                      props.currentGroup?.Participants ?? {}
                    )
                  )}
                />
              )
            )}
          </Styled.CalendarHeaderSubtitle>
        </GroupcalTooltip>
      )}
    </Styled.CalendarHeaderTitleContainer>
  )
}

function GroupPhotoElement(props: CalendarHeaderProps) {
  return (
    <ContainerWithBottomBadgeIcon
      showLock={
        !isGuest() &&
        isGroupOutOfTier(
          highestActiveTier(props.account),
          props.currentGroup,
          props.account
        )
      }
      topRightIcon={
        isGroupChannel(props.currentGroup) ? (
          <GroupChannelIcon width={18} height={18} />
        ) : undefined
      }
      marginRight={6}
    >
      <Avatar
        name=""
        asGroup
        size={'md'}
        img={props.currentGroup?.Photo}
        emptyPic={defaultGroupImage(props.currentGroup)}
        sx={{
          width: '40px',
          height: '40px'
        }}
      />
    </ContainerWithBottomBadgeIcon>
  )
}

interface NextPrevButtonsProps {
  dateRangeSelected?: (selected: Date | DateRange | undefined) => void
}

function NextPrevButtons(props: NextPrevButtonsProps) {
  const calendar = useCalendar()
  const appMode = useAppMode()
  const theme = useTheme()
  const currentView = calendar.view

  const visibleAreaStart = dayjs(calendar.fromISO)
  const visibleAreaEnd = dayjs(calendar.toISO).subtract(1, 'day')

  const center = visibleAreaStart.add(2, 'week')

  const startMonth = visibleAreaStart.format('MMM')
  const startDay = visibleAreaStart.format('DD')
  const startYear = visibleAreaStart.format('YYYY')

  const endMonth = visibleAreaEnd.format('MMM')
  const endDay = visibleAreaEnd.format('DD')
  const endYear = visibleAreaEnd.format('YYYY')

  let dateRangeString = ''
  const sameMonthAndYear = startMonth === endMonth && startYear === endYear

  if (sameMonthAndYear) {
    // Same month and same year
    dateRangeString = `${startMonth} ${startDay} - ${endDay}, ${endYear}`
  } else if (startYear === endYear) {
    // Different months but same year
    dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  } else {
    // Different months and different years
    dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  }

  switch (currentView) {
    case CalendarRange.LISTDAY:
    case CalendarRange.DAY:
      dateRangeString = visibleAreaStart.format('MMM DD, YYYY')
      break
    case CalendarRange.MONTH:
    case CalendarRange.LISTWEEK:
    case CalendarRange.CARDS:
      dateRangeString = center.format('MMM YYYY')
      break
  }

  if (isMobileDevice()) {
    dateRangeString =
      currentView === CalendarRange.MONTH || currentView === CalendarRange.CARDS
        ? center.format('MMM YYYY')
        : visibleAreaStart.format('MMM YYYY')
  }

  if (appMode.isCondensedHeight || appMode.isCondensedWidth) {
    // show only short month
    dateRangeString =
      currentView === CalendarRange.MONTH || currentView === CalendarRange.CARDS
        ? center.format('MMM')
        : visibleAreaStart.format('MMM')
  }

  return (
    <Grid2
      display="flex"
      flexDirection={'row'}
      alignItems={'center'}
      whiteSpace={'nowrap'}
    >
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        alignContent={'center'}
        flexDirection={'row'}
        sx={{
          flexGrow: 1,
          textAlign: 'center',
          height: appMode.isCondensedHeight ? 32 : 52,
          fontSize: isMobileDevice()
            ? '1rem'
            : appMode.isCondensedHeight
              ? '16px'
              : '1.25rem',
          borderRadius: 23,
          cursor: 'pointer',
          color: theme.palette.text.primary,

          minWidth: isMobileDevice()
            ? '50px'
            : appMode.isCondensedWidth || appMode.isCondensedHeight
              ? '90px'
              : undefined,

          '&:hover': {
            // border: `1px solid ${theme.palette.input.border.primary}`,

            backgroundColor: theme.palette.surface.hover
          }
        }}
      >
        <ViewDatesPicker
          mode={currentView === CalendarRange.WEEK ? 'range' : 'single'}
          defaultRange={{
            from: visibleAreaStart.toDate(),
            to: visibleAreaEnd.toDate()
          }}
          defaultDate={
            calendar.view === CalendarRange.MONTH ||
            calendar.view === CalendarRange.CARDS
              ? center.toDate()
              : visibleAreaStart.toDate()
          }
          onSelect={props.dateRangeSelected}
          withoutSelection={
            calendar.view === CalendarRange.MONTH ||
            calendar.view === CalendarRange.CARDS
          }
        >
          <Grid2
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            p={2}
            gap={0}
          >
            <div
              style={{
                paddingLeft: 8,
                paddingRight: 8,
                color: theme.palette.text.primary
              }}
            >
              {dateRangeString}
            </div>

            <CaretDownIcon />
          </Grid2>
        </ViewDatesPicker>
      </Box>
      <Box
        sx={{
          width: isMobileDevice() ? '32px' : undefined,
          height: isMobileDevice() ? '32px' : undefined
        }}
      >
        <Styled.IconButtonSlide
          sx={{
            marginRight: i18n.dir() === 'ltr' ? 0 : 2,
            marginLeft: i18n.dir() === 'ltr' ? 2 : 0
          }}
          data-prev={i18n.dir() === 'ltr' ? 1 : undefined}
          data-next={i18n.dir() === 'rtl' ? 1 : undefined}
          size="sm"
          onClick={calendar.prev}
        >
          <CaretDownIcon />
        </Styled.IconButtonSlide>
      </Box>
      <Box
        sx={{
          width: isMobileDevice() ? '32px' : undefined,
          height: isMobileDevice() ? '32px' : undefined
        }}
      >
        <Styled.IconButtonSlide
          data-next={i18n.dir() === 'ltr' ? 1 : undefined}
          data-prev={i18n.dir() === 'rtl' ? 1 : undefined}
          size="sm"
          onClick={calendar.next}
        >
          <CaretDownIcon />
        </Styled.IconButtonSlide>
      </Box>
    </Grid2>
  )
}

export default withObservables(['groupID'], (props: CalendarHeaderProps) => ({
  account: ObserveService.Account(),
  groups: ObserveService.Groups(),
  userSettings: ObserveService.UserSettings(),
  currentGroup: ObserveService.GroupByServerID(props.groupID)
}))(CalendarHeader)
